import {AfterViewInit, Component, Input, OnInit, OnDestroy} from '@angular/core';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import {SurveyService} from '../../../services/surveys.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ScriptLoaderService} from 'angular-google-charts';
import {AlertService} from '../../../services/general/alert.service';
import {ToastService} from '../../../services/general/toast.service';
import {PageEvent} from '@angular/material/paginator';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import {EChartsOption} from 'echarts';
import {AiService} from '../../../services/ai.service';
import {UserService} from '../../../services/user.service';
import {MatDialog} from '@angular/material/dialog';
import {SurveyReportService} from '../../../services/survey-report.service';
import {ACTIVE_REPORT_STATUSES, SurveyReport, SurveyReportDialogResult} from '../../../models/survey-report.model';
import {ModalSurveyReportComponent} from '../../../modals/modal-survey-report/modal-survey-report.component';
import {ModalSurveyReportProgressComponent} from '../../../modals/modal-survey-report-progress/modal-survey-report-progress.component';
import {ModalSurveyReportsComponent, SurveyReportsDialogResult} from '../../../modals/modal-survey-reports/modal-survey-reports.component';

@Component({
    selector: 'app-survey-all-responses',
    templateUrl: './survey-all-responses.component.html',
    styleUrls: ['./survey-all-responses.component.scss'],
    standalone: false
})
export class SurveyAllResponsesComponent implements OnInit, OnDestroy {

  constructor(
    public surveyService: SurveyService,
    public translateService: TranslateService,
    private loaderService: ScriptLoaderService,
    private toast: ToastService,
    private aiService: AiService,
    public userService: UserService,
    private surveyReportService: SurveyReportService,
    private dialog: MatDialog
  ) {

  }

  @Input() survey: any;
  @Input() surveyId: any;

  tabIndex = 0;
  loading = false;
  report: SurveyReport | null = null;
  reportStarting = false;
  private reportPollTimer: any = null;
  private downloadWhenReady = false;
  private reportPollFailures = 0;
  showPrivateInfo = false;
  questions: any[] = [];
  totalQuestions = 0;

  currentPageIndex: number = 0;
  pageSize: number = 1;

  chartOptions!: EChartsOption;
  readonly faClipboardList = faClipboardList;
  answeredCount = 0;
  draftCount = 0;
  notAnsweredCount = 0;

  filters: any = {
    custom: {},
    search: '',
    typologies: [],
    required: '',
    final_decision: '',
    section: ''
  };

  typologyOptions = [
    { value: 'CONSENSUS', label: 'service.survey-service.question-typologies.consensus.title' },
    { value: 'QUANTITATIVE', label: 'service.survey-service.question-typologies.quantitative.title' },
    { value: 'MULTIPLE_CHOICE', label: 'service.survey-service.question-typologies.multiple-choice.title' },
    { value: 'CHECKBOX', label: 'service.survey-service.question-typologies.checkbox.title' },
    { value: 'GRID', label: 'service.survey-service.question-typologies.grid.title' },
    { value: 'OPENED', label: 'service.survey-service.question-typologies.opened.title' },
    { value: 'CLASSIFICATION', label: 'service.survey-service.question-typologies.classification.title' },
    { value: 'WEIGHTED', label: 'service.survey-service.question-typologies.weighted.title' },
    { value: 'RANKING', label: 'service.survey-service.question-typologies.ranking.title' }
  ];

  decisionOptions = [
    { value: 'accepted', label: 'service.survey-service.question-final-decision-statuses.accepted.title' },
    { value: 'reproposed', label: 'service.survey-service.question-final-decision-statuses.reproposed.title' },
    { value: 'revised', label: 'service.survey-service.question-final-decision-statuses.revised.title' },
    { value: 'revised_split', label: 'service.survey-service.question-final-decision-statuses.revised_split.title' },
    { value: 'rejected', label: 'service.survey-service.question-final-decision-statuses.rejected.title' },
    { value: 'pending', label: 'components.decision-table.show-decision-table.not-evaluated-yet' }
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription!: Subscription;

  filteredQuestions: any[] = [];

  hasActiveCustomFilters(): boolean {
    const custom = this.filters.custom;
    if (!custom) return false;
    return Object.keys(custom).some(key => {
      const filterConfig = custom[key];
      if (filterConfig === undefined || filterConfig === null) return false;
      if (Array.isArray(filterConfig)) return filterConfig.length > 0;
      if (typeof filterConfig === 'object') {
        return (filterConfig.min !== null && filterConfig.min !== undefined && filterConfig.min !== '') ||
               (filterConfig.max !== null && filterConfig.max !== undefined && filterConfig.max !== '');
      }
      return filterConfig !== 'ALL';
    });
  }

  onFilterChange() {
    this.applyFilters();
  }

  applyFilters() {
    if (!this.questions) {
      this.filteredQuestions = [];
      return;
    }
    const filtersActive = this.hasActiveCustomFilters();
    this.filteredQuestions = this.questions.map(q => {
      const clonedQuestion = JSON.parse(JSON.stringify(q));

      if (clonedQuestion.responses && filtersActive) {
        clonedQuestion.responses = clonedQuestion.responses.filter(r => {
          return this.matchesCustomFilters(r);
        });
      }

      if (filtersActive) {
        this.recalculateQuestionMetrics(clonedQuestion);
      }
      return clonedQuestion;
    });
  }

  matchesCustomFilters(response: any): boolean {
    const custom = this.filters.custom;
    if (!custom || Object.keys(custom).length === 0) return true;

    const customFields = this.survey?.team_member_custom_fields || [];
    for (const fieldId of Object.keys(custom)) {
      const field = customFields.find(f => f.id == fieldId);
      if (!field) continue;

      const filterConfig = custom[fieldId];

      // Extract value
      let val = response.member_customs?.team_member_custom_fields?.[field.id];
      if (val === undefined) {
        val = response.member?.customs?.team_member_custom_fields?.[field.id];
      }

      if (field.type === 'boolean') {
        if (filterConfig !== 'ALL') {
          const expected = filterConfig === 'YES';
          if (val !== expected) return false;
        }
      } else if (field.type === 'number' || field.type === 'date') {
        const { min, max } = filterConfig;
        const isMinActive = min !== null && min !== undefined && min !== '';
        const isMaxActive = max !== null && max !== undefined && max !== '';

        let compareVal = val;
        let compareMin = min;
        let compareMax = max;

        if (field.type === 'date') {
          compareVal = val ? new Date(val).getTime() : undefined;
          compareMin = min ? new Date(min).getTime() : undefined;
          compareMax = max ? new Date(max).getTime() : undefined;

          if (compareVal) compareVal = new Date(new Date(compareVal).setHours(0, 0, 0, 0)).getTime();
          if (compareMin) compareMin = new Date(new Date(compareMin).setHours(0, 0, 0, 0)).getTime();
          if (compareMax) compareMax = new Date(new Date(compareMax).setHours(23, 59, 59, 999)).getTime();
        }

        if (isMinActive && (compareVal === undefined || compareVal === null || compareVal < compareMin)) return false;
        if (isMaxActive && (compareVal === undefined || compareVal === null || compareVal > compareMax)) return false;
      }
      else if (field.type === 'text') {
        if (filterConfig && filterConfig.length > 0 && !filterConfig.includes(val)) return false;
      }
    }
    return true;
  }

  recalculateQuestionMetrics(clonedQuestion: any) {
    const typology = clonedQuestion.typology;
    const responses = clonedQuestion.responses || [];

    // Initialize results if null
    if (!clonedQuestion.results) {
      clonedQuestion.results = {};
    }

    if (clonedQuestion.choices) {
      clonedQuestion.choices = clonedQuestion.choices.map(c => {
        let count = 0;
        if (typology === 'CHECKBOX') {
          count = responses.filter(r => Array.isArray(r.answer) && r.answer.includes(c.title)).length;
        } else {
          count = responses.filter(r => r.answer === c.title).length;
        }
        return { ...c, answers_count: count, responses_count: count };
      });
    }

    if (typology === 'CONSENSUS') {
      clonedQuestion.agreeCount = responses.filter(r => r.answer === 'AGREE').length;
      clonedQuestion.disagreeCount = responses.filter(r => r.answer === 'DISAGREE').length;
      clonedQuestion.abstainedCount = responses.filter(r => r.status === 'ABSTAINED' || r.answer === 'ABSTAINED').length;

      // Recalculate certainty and contradiction degree
      const agreeResponses = responses.filter(r => r.answer === 'AGREE');
      const disagreeResponses = responses.filter(r => r.answer === 'DISAGREE');
      const totalCount = responses.length;

      if (totalCount === 0) {
        clonedQuestion.results.certainty_degree = 0;
        clonedQuestion.results.contradiction_degree = 0;
        clonedQuestion.results.a_ratio = 0;
      } else {
        const favourWeight = agreeResponses.reduce((sum, r) => sum + ((r.confidence ?? r.customs?.confidence ?? r.score ?? r.customs?.score ?? 100) / 100), 0);
        const contraryWeight = disagreeResponses.reduce((sum, r) => sum + ((r.confidence ?? r.customs?.confidence ?? r.score ?? r.customs?.score ?? 100) / 100), 0);

        const favourRatio = favourWeight / totalCount;
        const contraryRatio = contraryWeight / totalCount;

        clonedQuestion.results.certainty_degree = Math.abs(favourRatio - contraryRatio) * 100;
        clonedQuestion.results.contradiction_degree = Math.min(favourRatio, contraryRatio) * 2 * 100;
        clonedQuestion.results.a_ratio = (agreeResponses.length / totalCount) * 100;
      }
    }

    if (typology === 'QUANTITATIVE') {
      const newIncidence = {};
      responses.forEach(r => {
        const val = r.answer;
        if (val !== undefined && val !== null && val !== 'N/A') {
          const key = Number(val).toFixed(2);
          newIncidence[key] = (newIncidence[key] || 0) + 1;
        }
      });
      clonedQuestion.incidence = newIncidence;

      const numericAnswers = responses
        .map(r => Number(r.answer))
        .filter(n => !isNaN(n));

      if (numericAnswers.length === 0) {
        clonedQuestion.results.mean = 0;
        clonedQuestion.results.median = 0;
        clonedQuestion.results.min = 0;
        clonedQuestion.results.max = 0;
        clonedQuestion.results.q1 = 0;
        clonedQuestion.results.q3 = 0;
        clonedQuestion.results.geometric_mean = 0;
        clonedQuestion.results.variance = 0;
      } else {
        numericAnswers.sort((a, b) => a - b);
        const sum = numericAnswers.reduce((a, b) => a + b, 0);
        const mean = sum / numericAnswers.length;

        const mid = Math.floor(numericAnswers.length / 2);
        const median = numericAnswers.length % 2 !== 0 ? numericAnswers[mid] : (numericAnswers[mid - 1] + numericAnswers[mid]) / 2;
        const min = numericAnswers[0];
        const max = numericAnswers[numericAnswers.length - 1];

        const getPercentile = (arr, p) => {
          const index = (arr.length - 1) * p;
          const lower = Math.floor(index);
          const upper = Math.ceil(index);
          const weight = index - lower;
          return arr[lower] * (1 - weight) + arr[upper] * weight;
        };
        const q1 = getPercentile(numericAnswers, 0.25);
        const q3 = getPercentile(numericAnswers, 0.75);

        const geomMean = Math.exp(numericAnswers.reduce((a, b) => a + Math.log(b > 0 ? b : 1), 0) / numericAnswers.length);
        const variance = numericAnswers.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / numericAnswers.length;

        const newResults = {
          ...clonedQuestion.results,
          mean,
          median,
          min,
          max,
          q1,
          q3,
          geometric_mean: geomMean,
          variance
        };

        clonedQuestion.results = newResults;
        clonedQuestion.quantitative_results = newResults;
      }
    }

    if (typology === 'GRID') {
      if (clonedQuestion.grid_rows && clonedQuestion.grid_columns) {
        clonedQuestion.grid_rows = clonedQuestion.grid_rows.map(row => {
          const newAnswersCount = {};
          clonedQuestion.grid_columns.forEach(col => {
            const count = responses.filter(r => {
              return Array.isArray(r.answer) && r.answer.some(a =>
                (a.row == row.id || a.grid_question_row_id == row.id) &&
                (a.column == col.id || a.grid_question_column_id == col.id)
              );
            }).length;
            newAnswersCount[col.id] = count;
          });
          return { ...row, answers_count: newAnswersCount };
        });
      }
    }

    if (typology === 'CLASSIFICATION') {
      if (clonedQuestion.grid_rows && clonedQuestion.grid_columns) {
        clonedQuestion.grid_rows = clonedQuestion.grid_rows.map(row => {
          const newAnswersCount = {};
          clonedQuestion.grid_columns.forEach(col => {
            const values: number[] = [];
            responses.forEach(r => {
              if (r.status !== 'ABSTAINED') {
                const ans = r.raw_answer ?? r.answer;
                if (ans && typeof ans === 'object' && ans[row.id] !== undefined && ans[row.id][col.id] !== undefined) {
                  const val = parseFloat(ans[row.id][col.id]);
                  if (!isNaN(val)) {
                    values.push(val);
                  }
                }
              }
            });
            const avg = values.length > 0 ? values.reduce((acc, b) => acc + b, 0) / values.length : 0;
            newAnswersCount[col.id] = Math.round(avg * 100) / 100;
          });
          return { ...row, answers_count: newAnswersCount };
        });
      }
    }
  }

  ngOnInit(): void {
    this.init();
    this.loadLatestReport();
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(searchText => {
      this.filters.search = searchText;
      this.currentPageIndex = 0;
      this.loadQuestions(1, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.stopReportPolling();
  }

  get reportIsActive(): boolean {
    return !!this.report && ACTIVE_REPORT_STATUSES.includes(this.report.status);
  }

  get reportStatusLabel(): string {
    if (!this.report) {
      return '';
    }
    const params = { done: this.report.progress_done, total: this.report.progress_total };
    switch (this.report.status) {
      case 'WAITING_ANALYSES':
        return this.translateService.instant('components.survey-report.status-waiting', params);
      case 'PROCESSING':
        return this.translateService.instant(
          this.report.progress_total ? 'components.survey-report.status-processing' : 'components.survey-report.status-queued',
          params
        );
      default:
        return this.translateService.instant('components.survey-report.status-queued');
    }
  }

  openReportProgress(confirmCancel = false): void {
    this.dialog.open(ModalSurveyReportProgressComponent, {
      data: { surveyId: this.surveyId, report: this.report, confirmCancel },
      panelClass: 'medium-dialog',
      width: '640px',
      maxWidth: '95vw',
    });
  }

  openReports(): void {
    if (this.reportStarting) {
      return;
    }
    this.dialog
      .open(ModalSurveyReportsComponent, {
        data: { surveyId: this.surveyId },
        panelClass: 'medium-dialog',
        width: '820px',
        maxWidth: '95vw',
      })
      .afterClosed()
      .subscribe((result: SurveyReportsDialogResult | undefined) => {
        if (result?.action === 'generate') {
          this.generateReport();
          return;
        }
        this.refreshReportState();
      });
  }

  private refreshReportState(): void {
    if (!this.surveyId) {
      return;
    }
    this.surveyReportService.latest(this.surveyId, (report) => {
      this.report = report;
      if (this.reportIsActive && !this.reportPollTimer) {
        this.startReportPolling();
      }
    });
  }

  generateReport(): void {
    if (this.reportIsActive) {
      this.openReportProgress();
      return;
    }
    if (this.reportStarting) {
      return;
    }
    this.reportStarting = true;
    const previousOptions = this.report?.options ?? null;
    this.surveyReportService.analysisStatus(this.surveyId, previousOptions?.ai_analysis_type || 'short', (analysis) => {
      this.reportStarting = false;
      this.dialog
        .open(ModalSurveyReportComponent, {
          data: { analysis, surveyId: this.surveyId, surveyTitle: this.survey?.title, previousOptions },
          panelClass: 'medium-dialog',
          width: '760px',
          maxWidth: '95vw',
        })
        .afterClosed()
        .subscribe((result: SurveyReportDialogResult | undefined) => {
          if (result) {
            this.reportStarting = true;
            this.startReport(result);
          }
        });
    }, () => this.reportStarting = false);
  }

  downloadReport(): void {
    if (this.report?.status === 'COMPLETED') {
      this.surveyReportService.download(this.surveyId, this.report);
    }
  }

  private startReport(result: SurveyReportDialogResult): void {
    const { mode, questionIds, options, revisionMode, revisionQuestionIds } = result;
    this.surveyReportService.start(this.surveyId, mode, questionIds, options, revisionMode, revisionQuestionIds, (report) => {
      this.reportStarting = false;
      this.report = report;
      this.downloadWhenReady = true;
      this.openReportProgress();
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant(mode === 'now' && revisionMode === 'now' ? 'components.survey-report.started' : 'components.survey-report.started-with-analyses')
      );
      this.startReportPolling();
    }, () => this.reportStarting = false);
  }

  private loadLatestReport(): void {
    if (!this.surveyId) {
      return;
    }
    this.surveyReportService.latest(this.surveyId, (report) => {
      this.report = report;
      if (this.reportIsActive) {
        this.startReportPolling();
      }
    });
  }

  private startReportPolling(): void {
    this.stopReportPolling();
    this.reportPollFailures = 0;
    this.reportPollTimer = setInterval(() => {
      this.surveyReportService.latest(this.surveyId, (report) => {
        this.reportPollFailures = 0;
        this.report = report;
        if (this.reportIsActive) {
          return;
        }
        this.stopReportPolling();
        this.handleReportFinished(report);
      }, () => {
        this.reportPollFailures++;
        if (this.reportPollFailures >= 5) {
          this.stopReportPolling();
          this.toast.error(
            this.translateService.instant('generic.error'),
            this.translateService.instant('components.survey-report.status-unavailable')
          );
        }
      });
    }, 5000);
  }

  private handleReportFinished(report: SurveyReport | null): void {
    const shouldNotify = this.downloadWhenReady;
    this.downloadWhenReady = false;
    if (!shouldNotify || !report) {
      return;
    }

    if (report.status === 'CANCELLED') {
      this.toast.info(
        this.translateService.instant('components.survey-report.cancelled'),
        this.translateService.instant('components.survey-report.cancelled-detail')
      );
      return;
    }

    if (report.status === 'FAILED') {
      this.toast.error(
        this.translateService.instant('components.survey-report.error'),
        report.error_message || this.translateService.instant('components.survey-report.error-generic')
      );
      return;
    }

    if (report.status !== 'COMPLETED') {
      return;
    }

    const counts = report.issues?.counts || {};
    const failed = (counts.failed || 0) + (counts.timeout || 0);
    const unavailable = failed + (counts.processing || 0) + (counts.missing || 0);
    if (unavailable > 0 || counts.render_error) {
      this.toast.warn(
        this.translateService.instant('components.survey-report.ready-with-issues'),
        this.translateService.instant('components.survey-report.issues-detail', {
          unavailable,
          failed: counts.failed || 0,
          timeout: counts.timeout || 0,
          render: counts.render_error || 0,
        })
      );
    } else {
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant('components.survey-report.ready')
      );
    }
    this.downloadReport();
  }

  private stopReportPolling(): void {
    if (this.reportPollTimer) {
      clearInterval(this.reportPollTimer);
      this.reportPollTimer = null;
    }
  }

  init() {
    this.loadQuestions(this.currentPageIndex + 1, this.pageSize); // Backend pages are 1-based
    this.renderChart();
  }

  loadQuestions(page: number, perPage: number) {
    this.loading = true;
    this.surveyService.surveyResponses(
      this.surveyId,
      page,
      perPage,
      this.filters,
      (result) => {
        this.questions = result.data;
        this.totalQuestions = result.total;
        this.applyFilters();
        this.loading = false;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  clearSearch() {
    this.filters.search = '';
    this.searchSubject.next('');
  }

  onTypologyChange() {
    this.currentPageIndex = 0;
    this.loadQuestions(1, this.pageSize);
  }

  onRequiredChange() {
    this.currentPageIndex = 0;
    this.loadQuestions(1, this.pageSize);
  }

  onDecisionChange() {
    this.currentPageIndex = 0;
    this.loadQuestions(1, this.pageSize);
  }

  onSectionChange() {
    this.currentPageIndex = 0;
    this.loadQuestions(1, this.pageSize);
  }

  getSections(): string[] {
    if (!this.survey || !this.survey.questions) return [];
    const sections = this.survey.questions
      .map((q: any) => q.customs?.section || q.section)
      .filter((s: any) => !!s);
    return Array.from(new Set(sections));
  }

  getSelectedTypologiesLabel(): string {
    if (!this.filters.typologies || this.filters.typologies.length === 0) {
      return this.translateService.instant('generic.all') || 'Tutte';
    }
    if (this.filters.typologies.length === 1) {
      const selected = this.typologyOptions.find(o => o.value === this.filters.typologies[0]);
      return selected ? this.translateService.instant(selected.label) : '';
    }
    return `${this.filters.typologies.length} selezionate`;
  }

  onPageChange(event: PageEvent) {
    this.currentPageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadQuestions(this.currentPageIndex + 1, this.pageSize);
  }

  onChangeStatus(event) {
    this.loading = true;
    const model = {
      status: this.survey?.status
    };
    this.surveyService.changeSurveyStatus(this.survey?.id, model, (result) => {
      if (this.survey?.status === 'EVALUATING') {
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('modals.modal-survey-details.resposes-enabled')
        );
      }
      if (this.survey?.status === 'CLOSED') {
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('modals.modal-survey-details.resposes-blocked')
        );
      }
      this.loading = false;
    }, (err) => {
      this.survey.status = err.error?.status;
      this.loading = false;
    });
  }

  toggleAnonymousData() {
    this.showPrivateInfo = !this.showPrivateInfo;
  }

  private renderChart(): void {
    const members = this.survey?.members || [];
    const respondents = members.filter(m => m.is_respondent === true);

    this.answeredCount = respondents.filter(m => m.response_status === 'SENT').length;
    this.draftCount = respondents.filter(m => m.response_status === 'DRAFT').length;
    this.notAnsweredCount = respondents.length - this.answeredCount - this.draftCount;

    const activeSlices = [this.answeredCount, this.notAnsweredCount, this.draftCount].filter(count => count > 0).length;

    this.chartOptions = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const label = params.name.replace(/\s*\(\d+\)\s*$/, '');
          return `${label}: ${params.value}`;
        }
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#666' }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: '50%',
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: activeSlices > 1 ? 2 : 0
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 14
          },
          labelLine: {
            show: false
          },
          data: [
            {
              value: this.answeredCount,
              name: `${this.translateService.instant('generic.who-has-responded')} (${this.answeredCount})`,
              itemStyle: { color: '#27ae60' }
            },
            {
              value: this.notAnsweredCount,
              name: `${this.translateService.instant('generic.who-has-not-responded')} (${this.notAnsweredCount})`,
              itemStyle: { color: '#e57373' }
            },
            {
              value: this.draftCount,
              name: `${this.translateService.instant('generic.answers.draft')} (${this.draftCount})`,
              itemStyle: { color: '#ffb74d' }
            },
          ].filter(item => item.value > 0)
        }
      ]
    };
  }

  get answersSent() {
    return this.survey?.members?.filter((m) => m.is_respondent === true && m.response_status === 'SENT') || [];
  }

  get participantsCount() {
    return this.survey?.members?.filter(m => m.is_respondent === true).length || 0;
  }

  get surveyIsPrivate() {
    return this.survey?.visibility_type == 'PRIVATE';
  }

  get currentUserInTeam() {
    return this.survey?.members?.find(
      (member: any) => member?.user_id == this.userService.userDetails.user_id,
    );
  }

  onAiAnalysisCompleted() {
    // console.log('AI Analysis completed event received in survey-all-responses');
    // console.log('Current page:', this.currentPageIndex, 'Total questions:', this.totalQuestions, 'Page size:', this.pageSize);

    // Calcola se ci sono altre domande dopo questa
    const nextPageIndex = this.currentPageIndex + 1;
    const maxPageIndex = Math.ceil(this.totalQuestions / this.pageSize) - 1;

    if (nextPageIndex <= maxPageIndex) {
      // Ci sono altre domande, triggera l'analisi per la domanda successiva senza navigare
      // console.log('Triggering AI analysis for next question (page', nextPageIndex, ') in background');

      // Carica la domanda successiva per ottenere il suo ID
      this.surveyService.surveyResponses(
        this.surveyId,
        nextPageIndex + 1, // Backend pages are 1-based
        this.pageSize,
        this.filters,
        (result) => {
          if (result.data && result.data.length > 0) {
            const nextQuestion = result.data[0];
            // console.log('Next question loaded:', nextQuestion.id);

            // Triggera l'analisi AI per la domanda successiva
            this.aiService.triggerSurveyAiAnalysis(
              this.surveyId,
              nextQuestion.id,
              this.survey?.attachments || [],
              nextQuestion.attachments || [],
              [], // survey_llm_analysis
              [], // question_llm_analysis
              [], // answer_llm_analysis
              false, // force: mai in avanzamento automatico, si rigenera solo
                     // ciò che manca per le domande successive
              (response) => {
                // console.log('AI analysis triggered for next question:', nextQuestion.id);
              },
              (error) => {
                console.error(' Error triggering AI analysis for next question:', error);
              }
            );
          }
        },
        (err) => {
          console.error('Error loading next question:', err);
        }
      );
    } else {
      // Era l'ultima domanda
      console.log('Analysis completed for all questions!');
    }
  }

  exportAllResponses() {
    this.loading = true;
    this.surveyService.exportAllResponses(this.surveyId, (result) => {
      this.loading = false;
      const blob = new Blob([result], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `survey_responses_${this.surveyId}.xlsx`);
    }, (err) => {
      this.loading = false;
      this.toast.error(
        this.translateService.instant('generic.error'),
        this.translateService.instant('generic.error-downloading-file')
      );
    });
  }
}
